import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import CustomScrollbars from "components/CustomScrollbars";
import Layout from "components/Layout";
import HomeSections from "components/landing/HomeSections";
import PartnerBand, { showsPartnerBand } from "components/landing/PartnerBand";
import { useGetUserQuery } from "features/auth/auth";
import { useGetPartnersQuery } from "features/partners/partnersApi";
import { useAppSelector } from "features/store";
import { useViewAs } from "hooks/useViewAs";
import useNavCatalog from "hooks/useNavCatalog";
import { useTheme } from "@mui/material/styles";
import {
  PHONE_LANDSCAPE_MATCH,
  VIEWPORT_2K_MIN_MEDIA,
  VIEWPORT_4K_MIN_MEDIA,
} from "config";

const LEGACY_STORAGE_PARAMS = [
  "prefix",
  "page",
  "search",
  "folder_type",
  "sort_field",
  "sort_order",
  "time_display",
] as const;

const HERO_HEADING = "Welcome to your CalibrateHCM Portal" as const;
const HERO_SUBHEADER = "Your tools and resources, in one place." as const;

export default function PlatformHomePage() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isClientView, href } = useViewAs();
  const { catalog, shown } = useNavCatalog();
  const showsBand = showsPartnerBand(shown);
  // A phone on its side has about 260px of page: the band follows the
  // products there instead of taking half of it.
  const pinsBand = !useMediaQuery(PHONE_LANDSCAPE_MATCH, { noSsr: true });

  const webAccessDenied = useAppSelector((s) => s.auth.webAccessDenied);
  const { isLoading } = useGetUserQuery(undefined, {
    skip: !!webAccessDenied,
  });

  const {
    data: partnersData,
    isSuccess: partnersLoaded,
    isError: partnersFailed,
  } = useGetPartnersQuery({ page_size: 1000 }, { skip: !showsBand });

  // Undefined until the directory answers; a failed request counts as none.
  const bandPartners = useMemo(() => {
    if (partnersFailed) return [];
    if (!partnersLoaded) return undefined;
    return (partnersData?.results ?? []).filter(
      (partner) => partner.logo_url && partner.logo_url.trim() !== "",
    );
  }, [partnersData, partnersLoaded, partnersFailed]);

  const queryString = searchParams.toString();

  useEffect(() => {
    const st = location.state as { initialPrefix?: string } | undefined;
    const suffix = queryString ? `?${queryString}` : "";
    if (st?.initialPrefix) {
      navigate(href(`/data-storage${suffix}`), { replace: true, state: st });
      return;
    }

    const hasLegacy = LEGACY_STORAGE_PARAMS.some((k) => searchParams.has(k));
    if (!hasLegacy) return;
    navigate(href(`/data-storage${suffix}`), { replace: true });
  }, [location.state, queryString, searchParams, href, navigate]);

  const accountSettingsPath = href("/account-settings");

  if (!isClientView && isLoading) {
    return (
      <Layout title="Home">
        <Box sx={{ py: 8, textAlign: "center", width: "100%" }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </Layout>
    );
  }

  const noProducts = catalog.sections.length === 0;

  const band = showsBand ? (
    <PartnerBand directoryPath={href("/partners")} partners={bandPartners} />
  ) : null;

  return (
    <Layout title="Home" noScroll>
      {/* The products scroll; the partner band stays at the bottom of the
          window, like the footer under it. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: { xs: 2.5, sm: 3 },
              pb: 3,
              [VIEWPORT_2K_MIN_MEDIA]: { px: 5, pt: 4, pb: 4 },
              [VIEWPORT_4K_MIN_MEDIA]: { px: 6 },
            }}
          >
            <Box sx={{ mb: 3, [VIEWPORT_2K_MIN_MEDIA]: { mb: 4 } }}>
              <Typography
                component="h1"
                sx={{
                  ...theme.typography.calibrateDisplay,
                  fontSize: { xs: 24, sm: 28 },
                  color: "calibrate.heroTitle",
                  lineHeight: 1.2,
                  [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 34 },
                  [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 38 },
                }}
              >
                {HERO_HEADING}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  ...theme.typography.calibrateDisplay,
                  fontSize: { xs: 16, sm: 18 },
                  fontWeight: 400,
                  color: "calibrate.heroSubtitle",
                  lineHeight: 1.35,
                  [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 22 },
                  [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 24 },
                }}
              >
                {HERO_SUBHEADER}
              </Typography>
            </Box>

            {noProducts ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 2,
                  maxWidth: 560,
                  mx: "auto",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>
                  You do not have access to any Calibrate products. Please
                  contact a Calibrate team member to help.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(accountSettingsPath)}
                >
                  Account settings
                </Button>
              </Box>
            ) : (
              <HomeSections catalog={catalog} />
            )}
          </Box>
          {!pinsBand && band}
        </CustomScrollbars>
        {pinsBand && band}
      </Box>
    </Layout>
  );
}
